import { colors } from '@/constants/colors';
import IconsLib from '@/utils/IconsLib';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IconType } from '@/utils/icons';
import useSaveChangesStore from '@/store/useSaveChangesStore';
import { useDistributorStatus } from '@/hooks/useDistributorStatus';
import { useResellerStatus } from '@/hooks/useResellerStatus';
import { useSurfaceStore } from '@/store/useSurfaceStore';
import { useTeamStore, actingHasPage } from '@/store/useTeamStore';
import { useCommunityTab } from '@/features/community/CommunityDock';
import NotificationBell from '@/components/Admin/NotificationBell';
import { useIsMobile } from '@/hooks/useIsMobile';


interface Props {
   active: string;
}

interface MenuLink {
   id: string;
   name: string;
   icon: IconType;
   link: string;
   beta?: boolean;
   /** Short tag beside the label, e.g. the "SmartFi" pill on ISP Billing. */
   pill?: string;
   /** Sub-items shown under the parent (desktop) and as their own rows in
    *  the phone sheet. `match` is the search string that marks one active. */
   children?: { id: string; name: string; link: string; match: string }[];
}

const ACCOUNT_ITEMS: MenuLink[] = [
   { id: 'subscription', name: 'Subscription', icon: 'user-fill', link: '/subscription' },
   { id: 'billing', name: 'Billing', icon: 'bank-card-line', link: '/billing' },
   { id: 'customization', name: 'Customization', icon: 'palette-fill', link: '/customization' },
   { id: 'api-integration', name: 'API & Integration', icon: 'code-s-slash-fill', link: '/api-integration' },
];

const VOUCHER_ITEMS: MenuLink[] = [
   { id: 'voucher', name: 'Dashboard', icon: 'home-5-fill', link: '/voucher' },
   { id: 'voucher-stores', name: 'Stores', icon: 'store-3-fill', link: '/voucher-stores' },
   { id: 'voucher-vouchers', name: 'Vouchers', icon: 'coupon-fill', link: '/voucher-vouchers' },
   { id: 'voucher-transactions', name: 'Transactions', icon: 'arrow-left-right-fill', link: '/voucher-transactions' },
   { id: 'voucher-payout', name: 'Payout', icon: 'bank-card-fill', link: '/voucher-payout' },
];

// The operator's own internet business. Deliberately mirrors the Piso WiFi
// shape — Dashboard first, then the things they touch daily — so somebody
// switching products is not relearning where anything lives.
// Aztech's own vocabulary (Ray, 2026-08-28). A CUSTOMER is the person or
// business; an ACCOUNT is one subscribed plan at one place, so a restaurant
// owner with two branches is one customer with two accounts. SERVER, not
// router, because here the box serves internet to other people's routers.
//
// Cashier is deferred — not needed this early.
// ISP Business. Screens are NEVER greyed out here — that was tried on
// 2026-08-31 and Ray cut it: "the disabled menu i changed my mind, no need, it
// looks weird." A brand new operator is guided by the setup wizard that takes
// over the dashboard until they are ready, which teaches the same order without
// putting locks on a menu.
const ISP_ITEMS: MenuLink[] = [
   { id: 'isp', name: 'Dashboard', icon: 'home-5-fill', link: '/isp' },
   { id: 'isp-customers', name: 'Customers', icon: 'user-fill', link: '/isp-customers' },
   { id: 'isp-accounts', name: 'Accounts', icon: 'layout-grid-fill', link: '/isp-accounts' },
   { id: 'isp-servers', name: 'Servers', icon: 'router-fill', link: '/isp-servers' },
   // Promoted out of Settings, 2026-08-31. Ray: "i change my mind, the internet
   // plans lets move it in the main menu since this is also important." Plans
   // are not configuration you set once — they are the price list, edited
   // whenever the business changes what it sells.
   { id: 'isp-plans', name: 'Internet plans', icon: 'pie-chart-fill', link: '/isp-plans' },
   { id: 'isp-invoices', name: 'Invoices', icon: 'bank-card-fill', link: '/isp-invoices' },
   // 🔴 THE PILL IS LEAD, NOT DECORATION. "Invoices" is what this operator
   // charges their subscribers; "Billing" is what SmartFi charges THEM. Two
   // numbers it would be catastrophic to confuse, one word apart in a menu
   // (Ray, 2026-09-01).
   {
      id: 'isp-billing',
      // "Subscriptions", not "Billing" (Ray, 2026-10-29): operators read
      // "Billing" as the bill THEY send, and asked which one it was.
      name: 'Subscriptions',
      icon: 'bank-fill',
      link: '/isp-billing',
   },
   { id: 'isp-settings', name: 'Settings', icon: 'settings-3-line', link: '/isp-settings' },
];

const WIFI_ITEMS_BASE: MenuLink[] = [
   { id: 'wifi', name: 'Dashboard', icon: 'home-5-fill', link: '/wifi' },
   { id: 'wifi-devices', name: 'Devices', icon: 'router-fill', link: '/wifi-devices' },
   { id: 'wifi-sessions', name: 'Sessions', icon: 'arrow-left-right-fill', link: '/wifi-sessions' },
   // Two sub-items (Ray 2026-10-14): the in-page tabs were "not obvious".
   {
      id: 'wifi-vouchers',
      name: 'Vouchers',
      icon: 'coupon-fill',
      link: '/wifi-vouchers?tab=counter',
      children: [
         // Over the counter first (Ray 2026-10-18).
         { id: 'wifi-vouchers-counter', name: 'Over the counter', link: '/wifi-vouchers?tab=counter', match: 'tab=counter' },
         { id: 'wifi-vouchers-epay', name: 'E-payments / GCash', link: '/wifi-vouchers?tab=epay', match: 'tab=epay' },
      ],
   },
   { id: 'wifi-data-plans', name: 'Data Plans', icon: 'pie-chart-fill', link: '/wifi-data-plans' },
   { id: 'wifi-networks', name: 'Networks', icon: 'layout-grid-fill', link: '/wifi-networks' },
   { id: 'wifi-partners', name: 'Partners', icon: 'bank-fill', link: '/wifi-partners' },
];

/** Never shared (Ray 2026-10-04): credit trading is money. */
export const TEAM_HIDDEN_NAV = new Set(['wifi-partners', 'wifi-distributors', 'wifi-resellers']);

const SideNav = ({ active }: Props) => {
   const { handleDiscard } = useSaveChangesStore();
   const [activeItem, setActiveItem] = useState(active);
   const [isSheetOpen, setIsSheetOpen] = useState(false);
   const location = useLocation();
   const navigate = useNavigate();
   const { isDistributor } = useDistributorStatus();
   const { isReseller } = useResellerStatus();
   const { surface } = useSurfaceStore();
   const acting = useTeamStore((st) => st.acting);
   const chat = useCommunityTab();
   // Phones: the bell lives in the bottom menu, not the header (Ray 2026-10-28).
   const phone = useIsMobile(768);

   const menu: MenuLink[] = useMemo(() => {
      if (acting) {
         // Helping another account: their Piso WiFi dashboard — only the
         // pages they ticked (Ray 2026-10-05), never credit trading.
         return WIFI_ITEMS_BASE.filter((m) => !TEAM_HIDDEN_NAV.has(m.id) && actingHasPage(acting, m.id));
      }
      if (surface === 'voucher-store') {
         return [...VOUCHER_ITEMS, ...ACCOUNT_ITEMS];
      }
      if (surface === 'isp') {
         // NO account items here. Ray, 2026-08-28: Subscription and Billing
         // belong to the Voucher Store — they describe that product's plan and
         // that product's bill. ISP Business is charged differently (₱30 per
         // active subscriber) and will carry its own when that is built.
         // Borrowing another product's billing screen would show an operator
         // numbers that have nothing to do with the business they are looking at.
         return ISP_ITEMS;
      }
      return [
         ...WIFI_ITEMS_BASE,
         ...(isDistributor
            ? [
                 {
                    id: 'wifi-distributors',
                    name: 'Distributors',
                    icon: 'user-fill' as const,
                    link: '/wifi-distributors',
                 } satisfies MenuLink,
              ]
            : []),
         ...(isReseller
            ? [
                 {
                    id: 'wifi-resellers',
                    name: 'Reseller',
                    icon: 'user-fill' as const,
                    link: '/wifi-resellers',
                 } satisfies MenuLink,
              ]
            : []),
      ];
   }, [surface, isDistributor, isReseller, acting]);

   const handleSelected = (id: string) => {
      setActiveItem(id);
   };

   useEffect(() => {
      const currentPath = location.pathname;
      // Compare the PATH only: "Vouchers" links to /wifi-vouchers?tab=counter,
      // and after a reload the item went un-highlighted (Ray 2026-10-18).
      const matchingItem = menu.find((item) => item.link.split('?')[0] === currentPath);
      handleDiscard();
      if (matchingItem) {
         setActiveItem(matchingItem.id);
      }
      setIsSheetOpen(false);
   }, [location.pathname, menu]);

   useEffect(() => {
      if (isSheetOpen) {
         document.body.style.overflow = 'hidden';
      }
      return () => {
         document.body.style.overflow = '';
      };
   }, [isSheetOpen]);

   const goHome = () => {
      setIsSheetOpen(false);
      navigate(
         surface === 'voucher-store'
            ? '/voucher'
            : surface === 'isp'
              ? '/isp'
              : '/wifi'
      );
   };

   const sheetItems = menu
      .filter((m) => m.id !== 'voucher' && m.id !== 'wifi')
      .flatMap((m) =>
         m.children
            ? [m, ...m.children.map((c) => ({ ...m, id: c.id, name: `${m.name} · ${c.name}`, link: c.link, children: undefined }))]
            : [m]
      );
   const dashboardId =
      surface === 'voucher-store' ? 'voucher' : surface === 'isp' ? 'isp' : 'wifi';
   const isHomeActive = activeItem === dashboardId;
   // Exactly one tab lights up: on the Community page that is Chat, not Home.
   const homeOn = isHomeActive && !chat.active && !isSheetOpen;
   const menuOn = isSheetOpen || (!isHomeActive && !chat.active);

   return (
      <>
         <Wrapper>
            <Link
               to={
                  surface === 'voucher-store'
                     ? '/voucher'
                     : surface === 'isp'
                       ? '/isp'
                       : '/wifi'
               }
            >
               <LogoWrapper>
                  <LogoBadge>
                     <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                        <path d="M5 12.55a11 11 0 0 1 14 0" />
                        <path d="M1.42 9a16 16 0 0 1 21.16 0" />
                        <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
                        <circle cx="12" cy="20" r="1.5" fill="white" stroke="none" />
                     </svg>
                  </LogoBadge>
                  <LogoWord>SmartFi</LogoWord>
               </LogoWrapper>
            </Link>

            {menu.map((item, index) => (
               <Item
                  $active={activeItem === item.id}
                  $group={!!item.children}
                  key={index}
                  onClick={() => handleSelected(item.id)}
               >
                  <Link to={item.link}>
                     <ItemContent className="nav-link">
                        <IconsLib
                           icon={
                              activeItem === item.id
                                 ? item.icon
                                 : (`${item.icon.replace('-fill', '-line')}` as IconType)
                           }
                           size="20px"
                           color={activeItem === item.id ? colors.SECONDARY : colors.GRAY1}
                        />
                        <ItemLabel $active={activeItem === item.id}>{item.name}</ItemLabel>
                        {item.beta ? <BetaPill>BETA</BetaPill> : null}
                        {item.pill ? (
                           <NavPill $active={activeItem === item.id}>
                              {item.pill}
                           </NavPill>
                        ) : null}
                     </ItemContent>
                  </Link>
                  {item.children && activeItem === item.id ? (
                     <SubList>
                        {item.children.map((c) => {
                           const on = location.search.includes(c.match);
                           return (
                              <SubItem key={c.id} to={c.link} $active={on} data-testid={`subnav-${c.id}`}>
                                 {c.name}
                              </SubItem>
                           );
                        })}
                     </SubList>
                  ) : null}
               </Item>
            ))}
         </Wrapper>

         <BottomTabBar $cols={(chat.on ? 3 : 2) + (phone ? 1 : 0)} data-testid="bottom-tab-bar">
            <TabBtn $active={homeOn} onClick={goHome}>
               <IconsLib
                  icon={homeOn ? 'home-5-fill' : 'home-5-line'}
                  size="22px"
                  color={homeOn ? colors.PRIMARY : colors.GRAY1}
               />
               <TabLabel $active={homeOn}>Home</TabLabel>
            </TabBtn>
            {/* Chat sits in the middle (Ray 2026-10-25). */}
            {chat.on && (
               <TabBtn $active={chat.active} onClick={chat.open} aria-label="Community chat">
                  <TabIcon>
                     <IconsLib
                        icon={chat.active ? 'chat-3-fill' : 'chat-3-line'}
                        size="22px"
                        color={chat.active ? colors.PRIMARY : colors.GRAY1}
                     />
                     {chat.unread > 0 && (
                        <TabBadge data-testid="tab-chat-unread">{chat.unread > 9 ? '9+' : chat.unread}</TabBadge>
                     )}
                  </TabIcon>
                  <TabLabel $active={chat.active}>Chat</TabLabel>
               </TabBtn>
            )}
            {phone && <NotificationBell tab />}
            <TabBtn $active={menuOn} onClick={() => setIsSheetOpen(true)}>
               <IconsLib
                  icon={isSheetOpen ? 'menu-fill' : 'menu-line'}
                  size="22px"
                  color={menuOn ? colors.PRIMARY : colors.GRAY1}
               />
               <TabLabel $active={menuOn}>Menu</TabLabel>
            </TabBtn>
         </BottomTabBar>

         <SheetOverlay $open={isSheetOpen} onClick={() => setIsSheetOpen(false)} />
         <Sheet $open={isSheetOpen} role="dialog" aria-hidden={!isSheetOpen}>
            <SheetHandle />
            <SheetHeader>
               <SheetTitle>Menu</SheetTitle>
               <SheetClose onClick={() => setIsSheetOpen(false)}>
                  <IconsLib icon="close-line" size="22px" color={colors.GRAY1} />
               </SheetClose>
            </SheetHeader>
            <SheetGrid>
               {sheetItems.map((item) => {
                  const isActive = activeItem === item.id;
                  return (
                     <SheetItem
                        key={item.id}
                        to={item.link}
                        onClick={() => {
                           handleSelected(item.id);
                           setIsSheetOpen(false);
                        }}
                        $active={isActive}
                     >
                        <SheetIconBox $active={isActive}>
                           <IconsLib
                              icon={
                                 isActive
                                    ? item.icon
                                    : (`${item.icon.replace('-fill', '-line')}` as IconType)
                              }
                              size="22px"
                              color={isActive ? 'white' : colors.PRIMARY}
                           />
                        </SheetIconBox>
                        <SheetItemLabel $active={isActive}>
                           {item.name}
                           {item.beta ? <BetaPill>BETA</BetaPill> : null}
                        {item.pill ? (
                              <NavPill $active={isActive}>{item.pill}</NavPill>
                           ) : null}
                        </SheetItemLabel>
                     </SheetItem>
                  );
               })}
            </SheetGrid>
         </Sheet>
      </>
   );
};

const BOTTOM_NAV_HEIGHT = 64;

const Wrapper = styled.div`
   padding: 1rem;
   display: flex;
   justify-content: flex-start;
   flex-direction: column;
   gap: 10px;
   border-right: 1px solid ${colors.GRAY14};
   background: var(--app-surface);
   height: 100vh;
   width: 250px;
   /* Stays in place while the page scrolls, like the top bar (Ray 2026-10-27); a long menu scrolls on its own. */
   position: sticky;
   top: 0;
   align-self: start;
   overflow-y: auto;
   box-sizing: border-box;

   @media (max-width: 768px) {
      display: none;
   }
`;

/* A group (an item with sub-items) paints blue on its HEADER only; the
   sub-items sit on the plain sidebar below it (Ray 2026-10-18). */
const Item = styled.div<{ $active: boolean; $group?: boolean }>`
   width: 100%;
   border-radius: 5px;
   background: ${({ $active, $group }) => ($active && !$group ? colors.PRIMARY : 'transparent')};
   :hover {
      border-radius: 5px;
      background-color: ${({ $active, $group }) => ($active && !$group ? colors.PRIMARY : $group ? 'transparent' : colors.GRAY14)};
   }
   ${({ $group, $active }) =>
      $group &&
      `
      > a { display: block; border-radius: 5px; background: ${$active ? colors.PRIMARY : 'transparent'}; }
      > a:hover { background: ${$active ? colors.PRIMARY : colors.GRAY14}; }
   `}
`;

const SubList = styled.div`
   display: flex;
   flex-direction: column;
   gap: 2px;
   margin: 4px 0 6px 28px;
   padding-left: 8px;
   border-left: 2px solid ${colors.GRAY13};
`;

const SubItem = styled(Link)<{ $active: boolean }>`
   /* && = beat the global a / a:hover colours in GlobalStyles */
   && {
      display: block;
      padding: 6px 10px;
      border-radius: 6px;
      font-size: 13px;
      color: ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY1)};
      font-weight: ${({ $active }) => ($active ? 700 : 500)};
      background: ${({ $active }) => ($active ? colors.GRAY14 : 'transparent')};
      text-decoration: none;
   }
   &&:hover {
      color: ${colors.PRIMARY};
      background: ${colors.GRAY14};
   }
`;

const BetaPill = styled.span`
   margin-left: 6px;
   padding: 2px 6px;
   background: linear-gradient(135deg, #f59e0b, #f97316);
   color: #fff;
   font-size: 9px;
   font-weight: 800;
   letter-spacing: 0.6px;
   line-height: 1;
   border-radius: 4px;
   vertical-align: middle;
   text-transform: uppercase;
`;
const ItemContent = styled.div`
   display: flex;
   align-items: center;
   padding: 10px;
   gap: 5px;
`;

const ItemLabel = styled.p<{ $active: boolean }>`
   color: ${({ $active }) => ($active ? 'white' : colors.GRAY1)};
   font-weight: ${({ $active }) => ($active ? 600 : 400)};
   font-size: 14px;
`;

const LogoWrapper = styled.div`
   cursor: pointer;
   margin-bottom: 1rem;
   display: flex;
   align-items: center;
   gap: 10px;
`;

const LogoBadge = styled.div`
   width: 34px;
   height: 34px;
   border-radius: 10px;
   background: linear-gradient(135deg, #3760f9 0%, #6a4dff 100%);
   display: grid;
   place-items: center;
   box-shadow: 0 4px 14px rgba(55, 96, 249, 0.45);
   flex-shrink: 0;
   svg {
      width: 20px;
      height: 20px;
   }
`;

const LogoWord = styled.span`
   font-weight: 800;
   font-size: 22px;
   letter-spacing: 0.02em;
   background: linear-gradient(90deg, ${colors.MAIN_DARK} 0%, #3760f9 100%);
   -webkit-background-clip: text;
   -webkit-text-fill-color: transparent;
   background-clip: text;
`;

const BottomTabBar = styled.nav<{ $cols: number }>`
   display: none;
   @media (max-width: 768px) {
      display: grid;
      grid-template-columns: repeat(${({ $cols }) => $cols}, 1fr);
      position: fixed;
      left: 0;
      right: 0;
      bottom: 0;
      height: calc(${BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom, 0px));
      padding-bottom: env(safe-area-inset-bottom, 0px);
      background: var(--app-surface);
      border-top: 1px solid ${colors.GRAY13};
      z-index: 998;
      box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.05);
   }
`;

const TabBtn = styled.button<{ $active: boolean }>`
   background: none;
   border: none;
   outline: none;
   cursor: pointer;
   display: flex;
   flex-direction: column;
   align-items: center;
   justify-content: center;
   gap: 2px;
   padding: 8px 4px;
   color: ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY1)};
   &:active {
      background: ${colors.GRAY14};
   }
`;

const TabIcon = styled.span`
   position: relative;
   display: inline-flex;
`;

const TabBadge = styled.span`
   position: absolute;
   top: -5px;
   right: -9px;
   min-width: 16px;
   height: 16px;
   padding: 0 4px;
   border-radius: 999px;
   display: grid;
   place-items: center;
   font-size: 10px;
   font-weight: 700;
   color: ${colors.WHITE};
   background: ${colors.RED};
`;

const TabLabel = styled.span<{ $active: boolean }>`
   font-size: 11px;
   font-weight: ${({ $active }) => ($active ? 600 : 500)};
   color: ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY1)};
`;

const SheetOverlay = styled.div<{ $open: boolean }>`
   display: none;
   @media (max-width: 768px) {
      display: block;
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.4);
      opacity: ${({ $open }) => ($open ? 1 : 0)};
      pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
      transition: opacity 0.2s ease;
      z-index: 999;
   }
`;

const Sheet = styled.div<{ $open: boolean }>`
   display: none;
   @media (max-width: 768px) {
      display: block;
      position: fixed;
      left: 0;
      right: 0;
      bottom: 0;
      background: var(--app-surface);
      border-radius: 20px 20px 0 0;
      z-index: 1000;
      padding: 8px 16px calc(20px + env(safe-area-inset-bottom, 0px));
      transform: translateY(${({ $open }) => ($open ? '0' : '100%')});
      transition: transform 0.28s cubic-bezier(0.22, 1, 0.36, 1);
      box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.15);
      max-height: 85vh;
      overflow-y: auto;
   }
`;

const SheetHandle = styled.div`
   width: 40px;
   height: 4px;
   background: ${colors.GRAY12};
   border-radius: 999px;
   margin: 4px auto 10px;
`;

const SheetHeader = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   padding: 4px 4px 12px;
`;

const SheetTitle = styled.h3`
   font-size: 16px;
   font-weight: 700;
   color: ${colors.MAIN_DARK90};
`;

const SheetClose = styled.button`
   background: none;
   border: none;
   padding: 4px;
   cursor: pointer;
   display: flex;
   align-items: center;
   justify-content: center;
`;

const SheetGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(4, 1fr);
   gap: 8px 4px;
   padding: 4px 0 8px;
   @media (max-width: 360px) {
      grid-template-columns: repeat(3, 1fr);
   }
`;

const SheetItem = styled(Link)<{ $active: boolean }>`
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 6px;
   padding: 10px 4px;
   border-radius: 12px;
   text-decoration: none;
   background: ${({ $active }) => ($active ? colors.BLUE_SUPER_LIGHT : 'transparent')};
   &:active {
      background: ${colors.GRAY14};
   }
`;

const SheetIconBox = styled.div<{ $active: boolean }>`
   width: 48px;
   height: 48px;
   border-radius: 14px;
   display: flex;
   align-items: center;
   justify-content: center;
   background: ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY14)};
`;

const SheetItemLabel = styled.span<{ $active: boolean }>`
   font-size: 11px;
   font-weight: ${({ $active }) => ($active ? 600 : 500)};
   color: ${({ $active }) => ($active ? colors.PRIMARY : colors.MAIN_DARK90)};
   text-align: center;
   line-height: 1.2;
`;

export default SideNav;


/** Distinguishes ISP "Billing" (SmartFi's bill to the operator) from Invoices. */
/**
 * 🔴 INVERTS WHEN THE ROW IS ACTIVE.
 *
 * The pill was blue text on a 13%-blue chip, which vanished the moment the row
 * itself turned solid blue — Ray, 2026-09-01: *"when active the billing menu,
 * the pill smartfi is hidden."* It is not decoration: "Invoices" is what an
 * operator charges their subscribers and "Billing" is what SmartFi charges
 * THEM, and the pill is the only thing on screen saying which is which. It has
 * to survive being selected.
 */
const NavPill = styled.span<{ $active?: boolean }>`
   margin-left: 6px;
   padding: 1px 7px;
   border-radius: 8px;
   font-size: 9px;
   font-weight: 800;
   letter-spacing: 0.04em;
   background: ${({ $active }) =>
      $active ? 'rgba(255, 255, 255, 0.22)' : `${colors.PRIMARY}22`};
   color: ${({ $active }) => ($active ? '#fff' : colors.PRIMARY)};
   white-space: nowrap;
`;
